import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useSettings } from 'src/@core/context/settingsContext'

import { switchBackend } from '~/configs/backendSwitch'
import { allowsRuntimeBackendSwitch, type BackendKey } from '~/configs/env'
import { buildCombo, comboToString, combosMatch } from '~/components/KeyboardShortcuts/comboUtils'
import { DOCS_URL, OPEN_NOTIFICATIONS_EVENT } from '~/components/KeyboardShortcuts/constants'
import { usePageShortcutsRegistration } from '~/components/KeyboardShortcuts/PageShortcutsContext'
import { useGShortcutRoutes } from '~/components/KeyboardShortcuts/useShortcutRoutes'
import { useOverlay, type OverlayControls } from '~/components/Overlay'
import useAnalytics from '~/debug/analytics'

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

/**
 * A single follow-up entry in an armed `G` or `B` sequence — the displayable key
 * (e.g. `'D'`), the localized action label (e.g. `'Dashboard'`), and the click
 * handler that performs the same action as pressing the key. The same handler
 * is invoked whether the user resolves the sequence via keyboard or by
 * clicking the corresponding row in `SequenceIndicator`.
 */
export type SequenceFollowUp = {
  key: string
  label: string
  onClick: () => void
}

/**
 * Pending-sequence state consumed by `SequenceIndicator`. The indicator stays
 * visible until the user resolves it by one of: pressing a follow-up key,
 * pressing a non-relevant key, pressing `Escape`, or clicking outside.
 */
export type SequenceState = {
  /** Already-pressed prefix key, uppercased for display (e.g. `'G'`). */
  prefix: string
  /** Available follow-ups (key + localized label + click handler). */
  followUps: SequenceFollowUp[]
}

type UseKeyboardShortcutsResult = {
  shortcutsOverlay: OverlayControls
  sequence: SequenceState | null
  /** Called by the indicator on click-away to dismiss the pending sequence. */
  dismissSequence: () => void
}

/**
 * Installs a global keydown listener that handles universal keyboard shortcuts:
 *   - `?` opens the help overlay (idempotent — `Escape` closes it)
 *   - `N` dispatches a `synop:open-notifications` CustomEvent
 *   - `.` toggles the sidebar (`navCollapsed` setting)
 *   - `Shift+D` opens the docs site in a new tab
 *   - `Escape` closes the help overlay / clears any pending sequence
 *   - `G` then `{d|v|s|c|r|a|m|b|,}` navigates to dashboard / vehicles / sites / chargers /
 *     reports / alerts / billing / settings
 *   - `B` then `{g|p|e}` switches the active backend (preview builds only)
 *
 * Sequence resolution model: pressing `G` (or `B` in preview) arms an indicator
 * that stays visible until explicit dismissal — there is NO auto-clear timeout.
 * Resolution paths:
 *   - Valid follow-up key → action fires + sequence dismissed
 *   - Non-relevant key → sequence dismissed, the key's own handler still runs
 *   - `Escape` → sequence dismissed (also closes the help overlay if open)
 *   - Click outside the indicator → sequence dismissed (wired in `SequenceIndicator`
 *     via MUI `ClickAwayListener` calling `dismissSequence`)
 *   - Click a follow-up row in the indicator → same `onClick` as the keyboard path
 *
 * Page-scoped shortcuts registered via `usePageShortcuts` fire when no global matches.
 *
 * Shortcuts are ignored when the focused element is an input, textarea, select, or
 * contentEditable region, or when any modifier key other than Shift is held.
 */
const useKeyboardShortcuts = (): UseKeyboardShortcutsResult => {
  const router = useRouter()
  const { settings, saveSettings } = useSettings()
  const pageRegistration = usePageShortcutsRegistration()
  const t = useTranslations('components.KeyboardShortcutsOverlay')

  // The keydown listener registers once for the lifetime of this component, so
  // each mutable input is captured via a ref so the closure can see the latest
  // value without re-registering.
  const routerRef = useRef(router)
  routerRef.current = router

  const shortcutsOverlay = useOverlay()
  const shortcutsOverlayRef = useRef(shortcutsOverlay)
  shortcutsOverlayRef.current = shortcutsOverlay

  const settingsRef = useRef({ settings, saveSettings })
  settingsRef.current = { settings, saveSettings }

  const pageRegistrationRef = useRef(pageRegistration)
  pageRegistrationRef.current = pageRegistration

  const analytics = useAnalytics('keyboard-shortcuts')
  const analyticsRef = useRef(analytics)
  analyticsRef.current = analytics

  const [sequence, setSequence] = useState<SequenceState | null>(null)
  /** Which prefix is currently armed (`'g'` or `'b'`), or `null` when idle. */
  const pendingSequenceRef = useRef<'g' | 'b' | null>(null)

  const dismissSequence = useCallback(() => {
    pendingSequenceRef.current = null
    setSequence(null)
  }, [])

  const track = useCallback(
    (combo: string, kind: 'global' | 'navigation' | 'page', extra?: Record<string, unknown>) => {
      analyticsRef.current.trackAction('shortcut.fired', { combo, kind, ...extra })
    },
    []
  )

  // Reusable nav/backend dispatch — invoked from both the keyboard path AND
  // from clicks on follow-up rows in `SequenceIndicator`. Each call ends by
  // dismissing the pending sequence so the indicator goes away cleanly.
  const navigateTo = useCallback(
    (route: string, key: string) => {
      shortcutsOverlayRef.current.closeOverlay()
      // Drop focus from whichever element the user last clicked. After
      // `router.push` unmounts the current page, the browser falls back to focus
      // on the next available element — and because the user just produced a
      // keyboard event (or sequence-row click), `:focus-visible` is "active", so
      // the fallback element renders a stale blue outline. Blurring here lets
      // focus fall to `<body>` (no ring), so the new page lands clean.
      ;(document.activeElement as HTMLElement | null)?.blur()
      routerRef.current.push(route)
      track(`g ${key}`, 'navigation')
      dismissSequence()
    },
    [track, dismissSequence]
  )

  const switchToBackend = useCallback(
    (next: BackendKey, key: string) => {
      shortcutsOverlayRef.current.closeOverlay()
      switchBackend(next)
      track(`b ${key}`, 'global', { backend: next })
      dismissSequence()
    },
    [track, dismissSequence]
  )

  // G-leader follow-ups. Each entry's key/label/route comes from
  // `useGShortcutRoutes` (R3.3) — sources the list from the user's flattened
  // sidebar nav (`useNavItems` + `flattenNavItems`) and applies both
  // JS-conditional gates (e.g. feature flags) AND CASL ACL filters. The
  // hook owns ordering, label localization, and the existence/ACL checks;
  // this `useMemo` just attaches the per-key `onClick` dispatch that drives
  // the same code path as a click on the indicator row.
  //
  // B-leader (backend switcher) below stays hardcoded — backends aren't in
  // the sidebar nav, and access is uniformly preview-only via
  // `allowsRuntimeBackendSwitch`.
  const gShortcutRoutes = useGShortcutRoutes()
  const gFollowups = useMemo<SequenceFollowUp[]>(
    () =>
      gShortcutRoutes.map(({ key, displayKey, label, route }) => ({
        key: displayKey,
        label,
        onClick: () => navigateTo(route, key)
      })),
    [gShortcutRoutes, navigateTo]
  )

  const bFollowups = useMemo<SequenceFollowUp[]>(
    () => [
      { key: 'G', label: t('backend_gamma'), onClick: () => switchToBackend('gamma', 'g') },
      { key: 'P', label: t('backend_prod'), onClick: () => switchToBackend('prod', 'p') },
      { key: 'E', label: t('backend_prodEu'), onClick: () => switchToBackend('prod_eu', 'e') }
    ],
    [t, switchToBackend]
  )

  // Lookup tables: lowercase key → follow-up entry. Used by the keyboard handler
  // to resolve a pending sequence in O(1).
  const gFollowupByKey = useMemo<Record<string, SequenceFollowUp>>(
    () => Object.fromEntries(gFollowups.map((f) => [f.key.toLowerCase(), f])),
    [gFollowups]
  )
  const bFollowupByKey = useMemo<Record<string, SequenceFollowUp>>(
    () => Object.fromEntries(bFollowups.map((f) => [f.key.toLowerCase(), f])),
    [bFollowups]
  )

  // Refs so the once-registered keydown handler always sees the latest arrays
  // even though they're recomputed on every render.
  const gFollowupsRef = useRef(gFollowups)
  gFollowupsRef.current = gFollowups
  const bFollowupsRef = useRef(bFollowups)
  bFollowupsRef.current = bFollowups
  const gFollowupByKeyRef = useRef(gFollowupByKey)
  gFollowupByKeyRef.current = gFollowupByKey
  const bFollowupByKeyRef = useRef(bFollowupByKey)
  bFollowupByKeyRef.current = bFollowupByKey

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) return
      if (isEditableTarget(event.target)) return
      if (event.metaKey || event.ctrlKey || event.altKey) return

      const key = event.key.toLowerCase()
      const pending = pendingSequenceRef.current

      // Tab (and Shift+Tab) must be allowed to default-handle while a sequence
      // is armed — otherwise the keyboard user can't reach the new focusable
      // follow-up rows in `SequenceIndicator`. Returning here without
      // `preventDefault` lets the browser advance focus normally and leaves
      // the sequence visible. (Per Copilot review on PR #2259.)
      if ((pending === 'g' || pending === 'b') && key === 'tab') return

      // Space / Enter on a focused follow-up row must NOT dismiss before the
      // button's native click fires — Space fires its click on keyup, so
      // dismissing on keydown unmounts (or starts unmounting) the button
      // before the activation completes. Only exempt when the event target is
      // inside the indicator (via the `data-sequence-indicator="true"` data
      // attribute on the Container); Space/Enter pressed anywhere else in the
      // app still dismisses as before. (Per Copilot review on PR #2259, same
      // thread as the Tab finding.)
      if (
        (pending === 'g' || pending === 'b') &&
        (key === ' ' || key === 'enter') &&
        event.target instanceof Element &&
        event.target.closest('[data-sequence-indicator="true"]') !== null
      ) {
        return
      }

      // Resolve a pending `B` backend-switcher leader. Same shape as the `G`
      // resolver below. Only reachable in preview builds — the leader can't be
      // armed in gamma/prod (see arming branch further down).
      if (allowsRuntimeBackendSwitch && pending === 'b') {
        const match = bFollowupByKeyRef.current[key]
        if (match && !event.shiftKey) {
          event.preventDefault()
          match.onClick() // closeOverlay + switchBackend + track + dismissSequence
          return
        }
        // Non-relevant key (or shift-modified) while B is armed — dismiss the
        // sequence and let the key fall through to its normal global handler
        // (e.g. `Escape`, `N`, `?` all keep working).
        dismissSequence()
      }

      // Resolve a pending `G` navigation leader. Same shape as backend resolver.
      if (pending === 'g') {
        const match = gFollowupByKeyRef.current[key]
        if (match && !event.shiftKey) {
          event.preventDefault()
          match.onClick() // closeOverlay + blur + router.push + track + dismissSequence
          return
        }
        // Non-relevant key (or shift-modified) — dismiss and fall through.
        dismissSequence()
      }

      if (event.key === '?') {
        event.preventDefault()
        shortcutsOverlayRef.current.openOverlay()
        dismissSequence()
        track('?', 'global')
        return
      }

      if (key === 'escape') {
        shortcutsOverlayRef.current.closeOverlay()
        dismissSequence()
        track('escape', 'global')
        return
      }

      if (key === 'n' && !event.shiftKey) {
        event.preventDefault()
        shortcutsOverlayRef.current.closeOverlay()
        document.dispatchEvent(new CustomEvent(OPEN_NOTIFICATIONS_EVENT))
        dismissSequence()
        track('n', 'global')
        return
      }

      if (key === '.') {
        event.preventDefault()
        const { settings: s, saveSettings: save } = settingsRef.current
        save({ ...s, navCollapsed: !s.navCollapsed })
        dismissSequence()
        track('.', 'global')
        return
      }

      if (key === 'd' && event.shiftKey) {
        event.preventDefault()
        window.open(DOCS_URL, '_blank', 'noopener,noreferrer')
        dismissSequence()
        track('shift+d', 'global')
        return
      }

      // Arm `G` leader. Sequence stays armed until explicit dismissal (key,
      // click, or Esc) — no auto-clear timeout.
      if (key === 'g' && !event.shiftKey) {
        event.preventDefault()
        pendingSequenceRef.current = 'g'
        setSequence({ prefix: 'G', followUps: gFollowupsRef.current })
        return
      }

      // Arm `B` backend-switcher leader (preview-only). Skipped in gamma/prod
      // so the bare letter stays available for any future page shortcut there.
      if (allowsRuntimeBackendSwitch && key === 'b' && !event.shiftKey) {
        event.preventDefault()
        pendingSequenceRef.current = 'b'
        setSequence({ prefix: 'B', followUps: bFollowupsRef.current })
        return
      }

      // Page-scoped shortcuts — checked last so globals always win.
      const registration = pageRegistrationRef.current
      if (registration) {
        const combo = buildCombo(event)
        const match = registration.shortcuts.find((s) => combosMatch(s.combo, combo))
        if (match) {
          event.preventDefault()
          // Close help overlay first if open, matching N/G-sequence behavior —
          // a keystroke discovered in the overlay should "use" the shortcut,
          // not silently fire behind it.
          if (shortcutsOverlayRef.current.isOpen) shortcutsOverlayRef.current.closeOverlay()
          match.handler()
          dismissSequence()
          track(comboToString(combo), 'page', { section: registration.sectionLabel ?? registration.pageLabel })
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [dismissSequence, track])

  return { shortcutsOverlay, sequence, dismissSequence }
}

export default useKeyboardShortcuts
